import React, { useState } from "react";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import { CreateResourceLibraryDTO } from "utils/helpers/models/resource-library/resource-library.dto";
import useResourceLibrary from "../useHooks";
import { toast } from "react-toastify";
import { warningToaster } from "utils/helpers/common/alert-service";
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const CreateResourceLibrary = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateResourceLibraryDTO>({
    defaultValues: {
      uploadImgae: undefined,
      uploadDocument: undefined,
      tags: [],
      UserType: [],
    },
  });

  const { uploadFile, createResourceLibrary } = useResourceLibrary();

  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState<string | null>(null);

  const goToBack = () => {
    navigate(-1);
  };

  const uploadProfilePic = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setValue("uploadImgae", file);
    const previewURL = URL.createObjectURL(file);
    setImagePreview(previewURL);
  };

  const handleDocUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setValue("uploadDocument", file);
    setDocumentName(file.name);
  };

  const onSubmit = async (data: CreateResourceLibraryDTO) => {
    if (!data.uploadImgae) {
      warningToaster("Image is required");
      return;
    }
    if (data.uploadImgae.size > MAX_FILE_SIZE) {
      warningToaster("Image cannot exceed 2 MB");
      return;
    }

    if (!data.uploadDocument) {
      warningToaster("Document is required");
      return;
    }
    if (data.uploadDocument.size > MAX_FILE_SIZE) {
      warningToaster("Document cannot exceed 2 MB");
      return;
    }

    let imageId = "";
    if (data.uploadImgae) {
      const formDataImage = new FormData();
      formDataImage.append("file", data.uploadImgae);
      const imageUploadResponse: any = await uploadFile(formDataImage);
      imageId = imageUploadResponse.id;
    }

    let documentId = "";
    if (data.uploadDocument) {
      const formDataDoc = new FormData();
      formDataDoc.append("file", data.uploadDocument);
      const docUploadResponse: any = await uploadFile(formDataDoc);
      documentId = docUploadResponse.id;
    }
    const payload: CreateResourceLibraryDTO = {
      title: data.title,
      tags: data.tags,
      // mimeType: data.mimeType,
      thumbnailId: imageId,
      fileId: documentId,
      isForHost: data.UserType?.includes("Host"),
      isForAttendee: data.UserType?.includes("Attendee"),
      isForAmbassador: data.UserType?.includes("Ambassador"),
    };
    await createResourceLibrary(payload);
  };

  const tags = ["Instructions", "Tips", "Guides", "More Instructions"];
  const userTypes = ["Attendee", "Host", "Ambassador"];

  return (
    <div>
      <h1 className="text-base text-primaryText font-bold flex items-center gap-3">
        <span
          onClick={goToBack}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Create Resource
      </h1>

      <div className="mt-4">
        <form onSubmit={handleSubmit(onSubmit)} className="">
          <div className="bg-white p-6 rounded-lg space-y-6">
            <div className="flex flex-col gap-1 w-full max-w-[320px]">
              <label className="custom-label">
                Resource name
              </label>
              <input
                type="text"
                placeholder="How to Improve Your Communication..."
                {...register("title", {
                  required: "Resource name is required",
                })}
                className="custom-input"
              />
              {errors.title && (
                <span className="text-red-500 text-sm">
                  {errors.title.message}
                </span>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-8 mt-4">
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <label className="custom-label">
                    Upload Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="imageUpload"
                    onChange={uploadProfilePic}
                  />
                  <label
                    htmlFor="imageUpload"
                    className="cursor-pointer text-forestText w-[145px] h-[50px] flex justify-center items-center bg-paperWhiteBg rounded-full"
                  >
                    + Upload
                  </label>
                </div>

                {imagePreview && (
                  <div className="relative w-12 h-12 mt-7">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover rounded"
                    />
                    <button
                      type="button"
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                      onClick={() => {
                        setImagePreview(null);
                        setValue("uploadImgae", undefined);
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <label className="custom-label">
                    Upload Document
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    id="docUpload"
                    onChange={handleDocUpload}
                  />
                  <label
                    htmlFor="docUpload"
                    className="cursor-pointer text-forestText w-[145px] h-[50px] flex justify-center items-center bg-paperWhiteBg rounded-full"
                  >
                    + Upload
                  </label>
                </div>

                {documentName && (
                  <div className="flex items-center space-x-2 mt-8">
                    <div className="text-sm">{documentName}</div>
                    <button
                      type="button"
                      className="bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                      onClick={() => {
                        setDocumentName(null);
                        setValue("uploadDocument", undefined);
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <p className="custom-label mb-1">
                Select Resource Tags
              </p>
              <div className="flex flex-wrap gap-4">
                {tags.map((tag) => (
                  <label
                    key={tag}
                    className="flex items-center gap-2 text-forestText text-lg"
                  >
                    <input
                      type="checkbox"
                      value={tag}
                      {...register("tags")}
                      className="custom-checkbox"
                    />
                    {tag}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="custom-label mb-1">
                Select User Type (will be displayed in those sections)
              </p>
              <div className="flex flex-wrap gap-4">
                {userTypes.map((type) => (
                  <label
                    key={type}
                    className="flex items-center gap-2 text-forestText text-lg"
                  >
                    <input
                      type="checkbox"
                      value={type}
                      {...register("UserType", {
                        required: "Please select at least one user type",
                      })}
                      className="custom-checkbox"
                    />
                    {type}
                  </label>
                ))}
              </div>
              {errors.UserType && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.UserType.message}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="mt-4 bg-darkSlateBlueBg text-white px-6 py-2 rounded"
          >
            Save
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateResourceLibrary;
